import { Reducer } from 'react'

const states = {
  start: 'START',
  parametersValid: 'PARAMETERS_VALID',
  parametersInvalid: 'PARAMETERS_INVALID',
  redirectCheckLoading: 'REDIRECT_CHECK_LOADING',
  redirectEmpty: 'REDIRECT_EMPTY',
  accountAvailable: 'ACCOUNT_AVAILABLE',
  accountMissing: 'ACCOUNT_MISSING',
  identitySignInFailed: 'IDENTITY_SIGN_IN_FAILED',
  providerSignInFailed: 'PROVIDER_SIGN_IN_FAILED',
  signOutRequested: 'SIGN_OUT_REQUESTED',
  memberNotAllowedToAccess: 'MEMBER_NOT_ALLOWED_TO_ACCESS',
  memberAssociationFailed: 'MEMBER_ASSOCIATION_FAILED',
  invitationDetailFailed: 'INVITATION_DETAIL_FAILED',
  redirectRequired: 'REDIRECT_REQUIRED',
  signingIntoProvider: 'SIGNING_INTO_PROVIDER',
  cancelled: 'CANCELLED',
  signedIntoProvider: 'SIGNED_INTO_PROVIDER',
  signingIntoIdentity: 'SIGNING_INTO_IDENTITY',
  signedIntoIdentity: 'SIGNED_INTO_IDENTITY',
  invitationDetailLoading: 'INVITATION_DETAIL_LOADING',
  invitationDetailAvailable: 'INVITATION_DETAIL_AVAILABLE',
  memberAllowedToAccess: 'MEMBER_ALLOWED_TO_ACCESS',
  associatingMemberWithResource: 'ASSOCIATING_MEMBER_WITH_RESOURCE',
  memberAssociated: 'MEMBER_ASSOCIATED',
  memberAlreadyAssociated: 'MEMBER_ALREADY_ASSOCIATED',
  memberNotAllowedToBeAssociated: 'MEMBER_NOT_ALLOWED_TO_BE_ASSOCIATED',
  invitationNotFoundWhileAssociating: 'INVITATION_NOT_FOUND_WHILE_ASSOCIATING',
  success: 'SUCCESS',
} as const

export type State = (typeof states)[keyof typeof states]

const events = {
  validateParametersTrue: 'VALIDATE_PARAMETERS_TRUE',
  validateParametersFalse: 'VALIDATE_PARAMETERS_FALSE',
  startRedirectCheck: 'START_REDIRECT_CHECK',
  successAndDataAvailable: 'SUCCESS_AND_DATA_AVAILABLE',
  successAndDataUnavailable: 'SUCCESS_AND_DATA_UNAVAILABLE',
  checkExistingAccountTrue: 'CHECK_EXISTING_ACCOUNT_TRUE',
  checkExistingAccountFalse: 'CHECK_EXISTING_ACCOUNT_FALSE',
  proceed: 'PROCEED',
  signOut: 'SIGN_OUT',
  cancel: 'CANCEL',
  failed: 'FAILED',
  success: 'SUCCESS',
  signInIdentity: 'SIGN_IN_IDENTITY',
  getInvitationDetail: 'GET_INVITATION_DETAIL',
  checkMemberAccessTrue: 'CHECK_MEMBER_ACCESS_TRUE',
  checkMemberAccessFalse: 'CHECK_MEMBER_ACCESS_FALSE',
  associateMember: 'ASSOCIATE_MEMBER',
  forbidden: '403',
  notFound: '404',
  conflict: '409',
} as const

export type Event = (typeof events)[keyof typeof events]

export const reducer: Reducer<State, Event> = (state, event) => {
  switch (state) {
    case states.start:
      if (event === events.validateParametersTrue) return states.parametersValid
      if (event === events.validateParametersFalse) return states.parametersInvalid
      break

    case states.parametersInvalid:
      if (event === events.cancel) return states.cancelled
      break

    case states.parametersValid:
      if (event === events.startRedirectCheck) return states.redirectCheckLoading
      break

    case states.redirectCheckLoading:
      if (event === events.successAndDataAvailable) return states.signedIntoProvider
      if (event === events.successAndDataUnavailable) return states.redirectEmpty
      break

    case states.redirectEmpty:
      if (event === events.checkExistingAccountTrue) return states.accountAvailable
      if (event === events.checkExistingAccountFalse) return states.accountMissing
      break

    case states.accountAvailable:
      switch (event) {
        case events.signOut:
          return states.signOutRequested
        case events.proceed:
          return states.signingIntoProvider
        case events.cancel:
          return states.cancelled
      }
      break

    case states.accountMissing:
      switch (event) {
        case events.proceed:
          return states.signingIntoProvider
        case events.cancel:
          return states.cancelled
      }
      break

    case states.signingIntoProvider:
      switch (event) {
        case events.failed:
          return states.providerSignInFailed
        case events.success:
          return states.signedIntoProvider
      }
      break

    case states.providerSignInFailed:
      if (event === events.signOut) return states.signOutRequested
      break

    case states.signedIntoProvider:
      if (event === events.signInIdentity) return states.signingIntoIdentity
      break

    case states.signingIntoIdentity:
      switch (event) {
        case events.failed:
          return states.identitySignInFailed
        case events.success:
          return states.signedIntoIdentity
      }
      break

    case states.identitySignInFailed:
      switch (event) {
        case events.cancel:
          return states.cancelled
        case events.signOut:
          return states.signOutRequested
      }
      break

    case states.signedIntoIdentity:
      if (event === events.getInvitationDetail) return states.invitationDetailLoading
      break

    case states.invitationDetailLoading:
      switch (event) {
        case events.failed:
          return states.invitationDetailFailed
        case events.success:
          return states.invitationDetailAvailable
      }
      break

    case states.invitationDetailFailed:
      if (event === events.cancel) return states.cancelled
      break

    case states.invitationDetailAvailable:
      if (event === events.checkMemberAccessTrue) return states.memberAllowedToAccess
      if (event === events.checkMemberAccessFalse) return states.memberNotAllowedToAccess
      break

    case states.memberNotAllowedToAccess:
      switch (event) {
        case events.cancel:
          return states.cancelled
        case events.signOut:
          return states.signOutRequested
      }
      break

    case states.memberAllowedToAccess:
      if (event === events.associateMember) return states.associatingMemberWithResource
      break

    case states.associatingMemberWithResource:
      switch (event) {
        case events.forbidden:
          return states.memberNotAllowedToBeAssociated
        case events.notFound:
          return states.invitationNotFoundWhileAssociating
        case events.conflict:
          return states.memberAlreadyAssociated
        case events.failed:
          return states.memberAssociationFailed
        case events.success:
          return states.memberAssociated
      }
      break

    case states.memberNotAllowedToBeAssociated:
      switch (event) {
        case events.signOut:
          return states.signOutRequested
        case events.cancel:
          return states.cancelled
      }
      break

    case states.memberAssociated:
    case states.memberAlreadyAssociated:
      if (event === events.proceed) return states.success
      break

    case states.memberAssociationFailed:
      if (event === events.cancel) return states.cancelled
      break

    case states.invitationNotFoundWhileAssociating:
      if (event === events.cancel) return states.cancelled
      break
  }

  return state
}
